import { GetReportsPresentationResponse } from '@appTypes/webscript';
import { REPORTS_BASE_URL } from '@config';
import {
  NEW_PROGRESS_REPORT,
  NEW_ROSTER_REPORT,
} from '@modules/reports/constants';
import { tagTypes } from '@modules/rtk/constants';
import { getPreferredName } from '@modules/shared/utils/preferredName';

export const sblSharedReportsManagerContainer = {
  Method: 'ExecuteContainerAPI',
  Container: 'SBLSharedReportsManagerAPI',
} as const;

const invalidateCustomReportsTag = (
  _result: unknown,
  error: unknown,
  { unitId }: { unitId: string },
) => {
  if (error) return [];

  return [
    {
      type: tagTypes.CustomReports,
      id: unitId,
    },
  ];
};

export type CustomReportsOperations = 'share' | 'unshare' | 'delete';

type CustomReportsPOSTApis =
  | 'ShareSelectedReports'
  | 'DeleteSelectedReports'
  | 'UnshareSelectedReports'
  | 'HideSelectedReports'
  | 'ShowSelectedReports';

export const postCustomReportBody = <T>(api: CustomReportsPOSTApis) => ({
  query: (arg: { unitId: string; payload: T }) => ({
    url: '',
    params: {
      ...sblSharedReportsManagerContainer,
      API: api,
    },
    method: 'POST',
    body: arg.payload,
  }),
  invalidatesTags: invalidateCustomReportsTag,
});

export interface CustomReportListItem {
  reportId: number;
  isShared: boolean;
  unit: string;
  title: string;
  userName: string;
  userMid: string;
  createdDate: string;
  designUri: string;
  runUri: string;
}

export interface UnitLink {
  unitId: number;
  unitName: string;
  linkCaption: string;
  hasReports: boolean;
  linkUri: string;
}

export interface NormalizedCustomReports {
  header: string;
  generated: string;
  unit: string;
  council: string;
  district: string;
  charteredOrganization: string;
  reports: CustomReportListItem[];
  unitLinks: UnitLink[];
}

export function transformCustomReportsResponse(
  response: GetReportsPresentationResponse,
): NormalizedCustomReports {
  const reports = response.reports.map(report => ({
    reportId: Number(report.report_id),
    isShared: report.is_report_shared,
    unit: report.unit,
    title: report.report_title,
    userName: getPreferredName({
      firstName: report.user_first_name,
      lastName: report.user_last_name,
    }),
    userMid: report.user_member_id,
    createdDate: report.created_date,
    designUri: report.design_uri,
    runUri: report.run_uri,
  }));

  const unitLinks = response.unit_links.map(report => ({
    unitId: Number(report.unit_id),
    unitName: report.unit_name,
    linkCaption: report.link_caption,
    hasReports: report.has_reports,
    linkUri: report.link_uri,
  }));

  return {
    header: response.header,
    generated: response.generated,
    unit: response.unit,
    council: response.council,
    district: response.district,
    charteredOrganization: response.chartered_organization,
    reports,
    unitLinks,
  };
}

export const getCustomReportButtonMessage = (type: CustomReportsOperations) =>
  `reports.customReport.${type}`;

export const getNewRosterReportUri = (type: number) =>
  `${REPORTS_BASE_URL}${NEW_ROSTER_REPORT}&UnitTypeID=${type}`;

export const getNewProgressReportUri = (type: number) =>
  `${REPORTS_BASE_URL}${NEW_PROGRESS_REPORT}&UnitTypeID=${type}`;
